import { Field, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Clipboard,
  Copy,
  InfoCircle,
  InfoCircleFill,
} from 'react-bootstrap-icons';
import toast from 'react-hot-toast';
import img404 from '../../../../assets/Images/404.jpg';
import noMessageIcon from '../../../../assets/Images/no_copied_image.jpg';
import { useStore } from '../../../../contexts/store-provider';
import {
  getTenantAccessFromLocalStorage,
  getTenantIdFromLocalStorage,
} from '../../../../helpers/localstorage-helper';
import {
  collectMatchingDeletedFiles,
  mergeUniqueProductFiles,
} from '../../../../helpers/product-file-delete-helper';
import { IActionFormatter } from '../../../../models/ICommon';
import {
  IFileIndices,
  IProductFiles,
  IProductImages,
} from '../../../../models/response/IProductResponse';
import DragDropFileInput from '../../../../shared-components/drag-drop-image-upload/drag-drop-image-uploader';
import FormikFormSearchSelectField from '../../../../shared-components/FormikFormHandlers/FormikFormSearchSelectField';
import {
  ExportIcon,
  FileCaptionIcon,
  PlusIcon,
  DeleteIcon,
} from '../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../translations/format-message';
import ImageTabView from './common-image-content-tab-view.tsx/common-image-tab-view';
import ImageContentView from './common-image-content-tab-view.tsx/common-image-content-view';
import { set } from 'lodash';
import { format } from 'path';
import CommonImageTabView from './common-image-content-tab-view.tsx/common-image-tab-view';
import Label from '../../../../shared-components/label/label';
import FormikFormSelect from '../../../../shared-components/FormikFormHandlers/FormikFormSelect';
import FormikFormInputMultipleSelect from '../../../../shared-components/FormikFormHandlers/FormikFormMultipleSelect';
import ConfirmDialog from '../confirm-dialog';

interface IProps {
  id: number;
  selectedProcessId: number;
  setSelectedProcessId: (processId: number) => void;
}

const CommonProcessTabContent = (props: IProps) => {
  const HEADING_MAX_LENGTH = 256;
  const { values, isValid, handleSubmit, setFieldValue, errors, resetForm } =
    useFormikContext<any>();
  //#region Local States/Variables
  const [showSave, setShowSave] = useState(true);
  const [isCopyPanelOpen, setIsCopyPanelOpen] = useState(true);
  const { id, selectedProcessId, setSelectedProcessId } = props;
  const { productStore, teplarPrintStore } = useStore();
  const [isCopied, setIsCopied] = useState(false);

  const {
    DeleteProductFileService,
    allProductImages,
    UploadProductFileService,
    UploadFileCopyService,
    allProductSearchSelectOptions,
    getCopiedImagesUrl,
    setCopiedImagesUrl,
    GetAllProductService,
    GetProductImagesByIdService,
    GetDimensionColumnService,
    addProductFileState,
    getDimensionTableColumnKeys,
    resetAddProductFileState,
    setNewDeletedFiles,
    setOldDeletedFiles,
  } = productStore;

  const [activeIndex, setActiveIndex] = useState<number>(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageIndex, setPreviewImageIndex] = useState<number>(0);
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isEnable, setIsEnable] = useState<boolean>(false);
  const [heading, setHeading] = useState<string>(() => {
    const currentHeading = values.Process?.find(
      (p: any) => p.ProcessId === selectedProcessId
    )?.fileIndices?.find((p: any) => p.TabIndex === activeIndex)?.FileDetail?.[0]
      ?.Heading;
    return currentHeading ?? '';
  });
  const headingInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [selectedImage, setSelectedImage] = useState<IProductFiles[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingDeleteData, setPendingDeleteData] = useState<any>(null);
  const { GetDimensionByProcessIdService } = productStore;

  const selectedProcessOptions = values?.Process?.filter(
    (p) =>
      p.ProcessId !== -1 &&
      p.ProcessId !== -2 &&
      p.ProcessId !== -3 &&
      p.ProcessId !== -4
  ).map((p) => ({
    id: p.ProcessId,
    value: p.ProcessName,
    disabled: false,
  }));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const commonTabLimit = 5;
  const commonTabCount =
    values?.Process?.find((p: any) => p.ProcessId === selectedProcessId)
      ?.fileIndices?.length ?? 0;
  const isCommonTabLimitReached = commonTabCount >= commonTabLimit;
  const hasActiveExistingCommonTab =
    values?.Process?.find(
      (p: any) => p.ProcessId === selectedProcessId
    )?.fileIndices?.some((fi: any) => fi.TabIndex === activeIndex) ?? false;
  //#endregion Local States/Variables

  //#region Hooks

  const selectedProcessIndx = useMemo(
    () =>
      values.Process.findIndex(
        (p: any) =>
          Number(p?.ProcessId) ===
          Number(values?.SelectedProcessForDimensionTable)
      ),
    [values?.Process, values?.SelectedProcessForDimensionTable]
  );

  useEffect(() => {
    setPreview(null);
    setFile(null);
    setFieldValue('file', null);
  }, [activeIndex]);

  useEffect(() => {
    if (
      values?.Process?.find((p) => p.ProcessId === selectedProcessId)
        ?.fileIndices?.length > 0
    ) {
      // let indicesLength = values.Process.find(
      //   (p) => p.ProcessId === selectedProcessId
      // )?.fileIndices?.length;
      // setActiveIndex(
      //   values.Process.find((p) => p.ProcessId === selectedProcessId)
      //     ?.fileIndices[indicesLength - 1]?.TabIndex
      // );
      // setSelectedImageIndex(
      //   values.Process.find((p) => p.ProcessId === selectedProcessId)
      //     ?.fileIndices[indicesLength - 1]?.TabIndex
      // );
    } else {
      let indicesLength = values?.Process.find(
        (p) => p.ProcessId === selectedProcessId
      )?.fileIndices?.length;
      if (indicesLength > 0) {
        setActiveIndex(
          values?.Process.find((p) => p.ProcessId === selectedProcessId)
            ?.fileIndices[indicesLength - 1]?.TabIndex + 1
        );
      }
      setSelectedImageIndex(-1);
    }
  }, [values?.Process, selectedProcessId]);

  useEffect(() => {
    setIsEnable(Boolean(heading.trim()) && Boolean(file));
  }, [heading, file]);

  const fileDetails = values.Process?.find(
    (p) => p.ProcessId === selectedProcessId
  )?.fileIndices?.find((p) => p.TabIndex === activeIndex)?.FileDetail;

  const validFiles = fileDetails ?? [];

  const allFilesAreNull =
    fileDetails &&
    fileDetails.length > 0 &&
    validFiles.every(
      (f: any) =>
        !f.S3FilePath ||
        f.S3FilePath === 'null' ||
        f.S3FilePath === 'undefined'
    );

  // console.log(fileDetails,fileDetails);
    

  const defaultCopyProductImageValue = useMemo(() => {
    return allProductSearchSelectOptions?.length > 0
      ? allProductSearchSelectOptions[0]?.Id
      : -1;
  }, [allProductSearchSelectOptions]);

  const isPreviewPdf = useMemo(() => {
    if (values.file?.type === 'application/pdf') return true;
    if (typeof preview === 'string') {
      const path = preview.split('?')[0].toLowerCase();
      if (path.endsWith('.pdf')) return true;
    }
    const lastSel = Array.isArray(selectedImage)
      ? selectedImage[selectedImage.length - 1]
      : (selectedImage as any);
    if (lastSel && (lastSel as any).FileType === 'application/pdf') return true;
    return false;
  }, [values.file, preview, selectedImage]);

  useEffect(() => {
    setFieldValue(
      'SelectedProcessForDimensionTable',
      values?.Process?.filter(
        (p) =>
          p.ProcessId !== -1 &&
          p.ProcessId !== -2 &&
          p.ProcessId !== -3 &&
          p.ProcessId !== -4
      ).map((p) => ({
        id: p.ProcessId,
        value: p.ProcessName,
        disabled: false,
      }))[0]?.id || -1
    );
  }, []);

  const getTabLockedType = useCallback(() => {
    const currentProcess = values.Process?.find(
      (p: any) => p.ProcessId === selectedProcessId
    );
    if (!currentProcess?.fileIndices) return null;

    const currentTab = currentProcess.fileIndices.find(
      (tab: any) => tab.TabIndex === activeIndex
    );
    if (!currentTab?.FileDetail) return null;

    for (const file of currentTab.FileDetail) {
      if (file.FileType === 'application/pdf') return 'pdf';
      if (
        file.FileType?.startsWith('image/') ||
        (file.S3FilePath && !file.S3FilePath.toLowerCase().endsWith('.pdf'))
      )
        return 'image';
    }
    return null;
  }, [values.Process, selectedProcessId, activeIndex]);

  const hasDuplicateHeadingInCommonTabs = useCallback(
    (value: string) => {
      const normalized = String(value || '').trim().toLowerCase();
      if (!normalized) return false;

      const duplicateInCommon =
        values?.Process?.find((p: any) => p.ProcessId === selectedProcessId)
          ?.fileIndices?.some(
            (fi: any) =>
              Number(fi?.TabIndex) !== Number(activeIndex) &&
              String(fi?.FileDetail?.[0]?.Heading || '')
                .trim()
                .toLowerCase() === normalized
          ) ?? false;
      return duplicateInCommon;
    },
    [values?.Process, selectedProcessId, activeIndex]
  );

  const isDuplicateHeading = useMemo(() => {
    return hasDuplicateHeadingInCommonTabs(heading);
  }, [heading, hasDuplicateHeadingInCommonTabs]);

  // True when the active tab already has a saved file but the heading has been cleared
  const isHeadingEmptyWithFile = useMemo(() => {
    const hasFile = getTabLockedType() !== null;
    return hasFile && String(heading || '').trim() === '';
  }, [heading, getTabLockedType]);

  const getCommonImageTabHeading = (productImage: IFileIndices) => {
    return String(productImage?.FileDetail?.[0]?.Heading || '').trim();
  };

  //#endregion Hooks

  //#region Local Functions
  const updateActiveCommonTabHeading = (value: string) => {
    const selectedProcessIndex = values.Process?.findIndex(
      (p: any) => Number(p?.ProcessId) === Number(selectedProcessId)
    );
    if (selectedProcessIndex < 0) return;

    const currentFileIndices =
      values.Process?.[selectedProcessIndex]?.fileIndices ?? [];
    const selectedFileIndex = currentFileIndices.findIndex(
      (file: any) => Number(file?.TabIndex) === Number(activeIndex)
    );
    if (selectedFileIndex < 0) return;

    const updatedFileIndices = currentFileIndices.map((file: any) => {
      if (Number(file?.TabIndex) !== Number(activeIndex)) return file;

      return {
        ...file,
        FileDetail:
          file?.FileDetail?.map((detail: IProductFiles) => ({
            ...detail,
            Heading: value,
          })) ?? [],
      };
    });

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices`,
      updatedFileIndices
    );
    setFieldValue(
      'CommonProcess',
      updatedFileIndices.filter((fi: any) => fi.IsDefault || fi.isDefault)
    );
  };

  const handleClick = (index: number, productImage?: IProductFiles[]) => {
    if (productImage?.length > 0) {
      setSelectedImageIndex(index);
      setSelectedImage(productImage);
      setCopiedImagesUrl([]);
      setIsCopied(false);
      setFieldValue('SelectedCopyProductImage', -1);
    } else {
      setSelectedImageIndex(-1);
      setSelectedImage([]);
    }
    const clickedHeading = values.Process?.find(
      (p: any) => p.ProcessId === selectedProcessId
    )?.fileIndices?.find((fi: any) => fi.TabIndex === index)?.FileDetail?.[0]
      ?.Heading;
    setHeading(clickedHeading ?? '');
    setActiveIndex(index);
    // setIsCopyPanelOpen(() => false);
  };

  const handlePaste = (setFieldValue: any, selectedImage: any) => {
    setFieldValue('file', null);
    setPreview(selectedImage?.S3FilePath);
    setIsEnable(true);
  };

  const handleCopy = (productObject: IProductImages) => {
    setIsCopyPanelOpen(() => true);
  };
  const [isOpen, setIsOpen] = useState(false);
  const removeCommonTabAndFocus = (
    selectedProcessIndex: number,
    deletedTabIndex: number
  ) => {
    const currentIndices =
      values.Process?.[selectedProcessIndex]?.fileIndices ?? [];
    const updatedIndices = currentIndices
      .filter((fi: any) => fi.TabIndex !== deletedTabIndex)
      .map((fi: any, idx: number) => ({
        ...fi,
        TabIndex: idx + 1,
      }));

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices`,
      updatedIndices
    );
    setFieldValue(
      'CommonProcess',
      updatedIndices.filter((fi: any) => fi.IsDefault || fi.isDefault)
    );

    if (updatedIndices.length === 0) {
      setActiveIndex(-1);
      setSelectedImageIndex(-1);
      setSelectedImage([]);
      setHeading('');
      return;
    }

    const nextTab =
      updatedIndices.find((fi: any) => fi.TabIndex === deletedTabIndex) ||
      updatedIndices[updatedIndices.length - 1];
    const nextIndex = nextTab?.TabIndex ?? 1;
    setActiveIndex(nextIndex);
    setSelectedImageIndex(nextIndex);
    setSelectedImage(nextTab?.FileDetail ?? []);
    setHeading(nextTab?.FileDetail?.[0]?.Heading ?? '');
  };

  const handleAddNewCommonTab = () => {
    if (isCommonTabLimitReached) return;

    const nextIndex =
      (values.Process?.find(
        (p: any) => p.ProcessId === selectedProcessId
      )?.fileIndices?.reduce(
        (max: number, fi: any) => Math.max(max, fi?.TabIndex || 0),
        0
      ) ?? 0) + 1;

    setSelectedImageIndex(-1);
    setSelectedImage([]);
    setHeading('');
    if (headingInputRef.current) {
      headingInputRef.current.value = '';
    }
    setPreview(null);
    setFile(null);
    setFieldValue('file', null);
    setActiveIndex(nextIndex);
    setCopiedImagesUrl([]);
    setIsCopied(false);
    setFieldValue('SelectedCopyProductImage', -1);
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteData) return;

    if (pendingDeleteData.action === 'file') {
      const {
        values,
        selectedProcessId,
        fileIndex,
        tabIndex,
        setFieldValue,
      } = pendingDeleteData;
      const selectedProcessIndex = values.Process?.findIndex(
        (p: any) => p.ProcessId === selectedProcessId
      );
      const selectedFileIndex = (
        values.Process[selectedProcessIndex]?.fileIndices ?? []
      ).findIndex((file: any) => file.TabIndex === tabIndex);

      const currentTabData = values.Process[selectedProcessIndex]?.fileIndices?.find(
        (file: any) => file.TabIndex === tabIndex
      );

      const foundFile = currentTabData?.FileDetail?.find(
        (f: any, i: number) => i === fileIndex
      );

      const { newDeletedFiles, oldDeletedFiles } = collectMatchingDeletedFiles(
        values.Process,
        foundFile,
        {
          commonOnly: true,
          tabHeading: currentTabData?.FileDetail?.[0]?.Heading,
          exactIdentityOnly: true,
        }
      );

      const mergedNewDeleted = mergeUniqueProductFiles(
        values?.NewDeletedFiles || [],
        newDeletedFiles
      );
      const mergedOldDeleted = mergeUniqueProductFiles(
        values?.OldDeletedFiles || [],
        oldDeletedFiles
      );

      setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
      setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

      setNewDeletedFiles(mergedNewDeleted);
      setOldDeletedFiles(mergedOldDeleted);

      const remainingFiles = [
        ...(currentTabData?.FileDetail?.filter(
          (_: any, i: number) => i !== fileIndex
        ) || []),
      ];

      if (remainingFiles.length > 0) {
        setFieldValue(
          `Process.${selectedProcessIndex}.fileIndices.${selectedFileIndex}.FileDetail`,
          remainingFiles
        );

        const updatedFileIndices = (
          values.Process[selectedProcessIndex]?.fileIndices ?? []
        ).map((file: any, index: number) =>
          index === selectedFileIndex
            ? { ...file, FileDetail: remainingFiles }
            : file
        );
        setFieldValue(
          'CommonProcess',
          updatedFileIndices.filter((fi: any) => fi.IsDefault || fi.isDefault)
        );
        setSelectedImage(remainingFiles);
      } else {
        removeCommonTabAndFocus(selectedProcessIndex, tabIndex);
      }

      setShowConfirm(false);
      setPendingDeleteData(null);
      return;
    }

    const { values, selectedProcessId, productImage, setFieldValue } =
      pendingDeleteData;
    const selectedProcessIndex = values.Process?.findIndex(
      (p: any) => p.ProcessId === selectedProcessId
    );

    let foundIndices = values.Process[selectedProcessIndex].fileIndices?.find(
      (f) => f.TabIndex === productImage.TabIndex
    );
    let oldDeletedFiles = [];
    let newDeletedFiles = [];
    const targetCommonFiles = Array.isArray(productImage?.FileDetail)
      ? productImage.FileDetail
      : [];
    const targetCommonFileIds = new Set(
      targetCommonFiles
        .map((file: any) => Number(file?.FileId || 0))
        .filter((fileId: number) => fileId > 0)
    );
    const targetCommonPaths = new Set(
      targetCommonFiles
        .map((file: any) => file?.FilePath || file?.S3FilePath)
        .filter(Boolean)
    );
    const targetCommonHeadings = new Set(
      targetCommonFiles
        .map((file: any) => String(file?.Heading || '').trim())
        .filter(Boolean)
    );

    // console.log('--- GLOBAL DELETION START ---');

    const deletedFile = productImage.FileDetail?.[0];
    const deletedPath = deletedFile?.FilePath;
    const deletedId = deletedFile?.FileId;
    const isCommon = productImage.IsDefault === true;

    // console.log(`Type: ${isCommon ? 'COMMON' : 'NORMAL'}, ID: ${deletedId}, Path: ${deletedPath}`);

    // console.log('[DELETE DEBUG] Current Formik values.Process common files:', values.Process?.map((p: any) => ({
    //   ProcessId: p.ProcessId,
    //   commonFiles: p.fileIndices?.filter((f: any) => f.IsDefault).map((f: any) => ({
    //     TabIndex: f.TabIndex,
    //     FileIds: f.FileDetail?.map((fd: any) => fd.FileId),
    //     FilePath: f.FileDetail?.[0]?.FilePath
    //   }))
    // })));

    const isSameDeletedCommonFile = (file: any) => {
      if (!file) return false;

      const fileId = Number(file?.FileId || 0);
      const filePath = file?.FilePath || file?.S3FilePath;
      const heading = String(file?.Heading || '').trim();

      return (
        (fileId > 0 && targetCommonFileIds.has(fileId)) ||
        (filePath && targetCommonPaths.has(filePath)) ||
        (heading && targetCommonHeadings.has(heading))
      );
    };

    values.Process.forEach((process: any) => {
      process.fileIndices?.forEach((fi: any) => {
        if (fi.IsDimension) return;

        // Match the tab to be deleted
        let isTabMatch = false;
        if (isCommon) {
          const fiHeading = fi.FileDetail?.[0]?.Heading;
          const targetHeading = productImage.FileDetail?.[0]?.Heading;
          if (fiHeading && targetHeading) {
            isTabMatch =
              fi.IsDefault &&
              String(fiHeading).trim().toLowerCase() ===
                String(targetHeading).trim().toLowerCase();
          } else {
            // For common tabs, match against every FileDetail entry from GetById.
            isTabMatch =
              fi.IsDefault && fi.FileDetail?.some(isSameDeletedCommonFile);
          }
        } else {
          // For regular tabs, match if it contains the specific file being deleted
          isTabMatch =
            !fi.IsDefault &&
            fi.FileDetail?.some(
              (f) =>
                f.FileId === deletedId ||
                (f.FilePath && f.FilePath === deletedPath)
            );
        }

        if (isTabMatch) {
          fi.FileDetail?.forEach((file: any) => {
            if (file.IsNew) newDeletedFiles.push(file);
            else oldDeletedFiles.push(file);
          });
        }
      });
    });

    // console.log('Final collected Old Files:', oldDeletedFiles.map(f => f.FileId));
    // console.log('Final collected New Files:', newDeletedFiles.map(f => f.FileId));

    const uniqueOldDeleted = mergeUniqueProductFiles(oldDeletedFiles);
    const uniqueNewDeleted = mergeUniqueProductFiles(newDeletedFiles);
    const mergedNewDeleted = mergeUniqueProductFiles(
      values?.NewDeletedFiles || [],
      uniqueNewDeleted
    );
    const mergedOldDeleted = mergeUniqueProductFiles(
      values?.OldDeletedFiles || [],
      uniqueOldDeleted
    );

    setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
    setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

    setNewDeletedFiles(mergedNewDeleted);
    setOldDeletedFiles(mergedOldDeleted);

    // console.log(
    //   'Deleted Files Information:',
    //   [...uniqueNewDeleted, ...uniqueOldDeleted].map((f) => ({
    //     FileId: f.FileId,
    //     Heading: f.Heading,
    //   }))
    // );

    const updatedProcesses = values.Process.map((p: any) => {
      const filtered = (p.fileIndices ?? []).filter((fi: any) => {
        if (!fi) return false;
        const isCommon = fi.IsDefault || fi.isDefault;
        if (isCommon) {
          const fiHeading = fi.FileDetail?.[0]?.Heading;
          const targetHeading = productImage.FileDetail?.[0]?.Heading;
          let isMatch = false;
          if (fiHeading && targetHeading) {
            isMatch =
              String(fiHeading).trim().toLowerCase() ===
              String(targetHeading).trim().toLowerCase();
          } else {
            isMatch = fi.FileDetail?.some(isSameDeletedCommonFile);
          }
          return !isMatch;
        }

        // For non-common tabs, if it's the current process being edited, match by TabIndex
        if (p.ProcessId === selectedProcessId) {
          const isMatch = fi.TabIndex === productImage.TabIndex;
          if (isMatch)
            //  console.log(`[FILTER LOG] Removing Regular Tab from Process ${p.ProcessId}: TabIndex ${fi.TabIndex}`);
            return !isMatch;
        }
        return true;
      });

      // Re-index remaining tabs for this process
      return {
        ...p,
        fileIndices: filtered.map((fi: any, idx: number) => ({
          ...fi,
          TabIndex: idx + 1,
        })),
      };
    });

    setFieldValue('Process', updatedProcesses);

    // Also update CommonProcess shortcut field
    const currentProcessUpdatedIndices =
      updatedProcesses.find((p: any) => p.ProcessId === selectedProcessId)
        ?.fileIndices ?? [];
    setFieldValue(
      'CommonProcess',
      currentProcessUpdatedIndices.filter(
        (fi: any) => fi.IsDefault || fi.isDefault
      )
    );

    if (currentProcessUpdatedIndices.length > 0) {
      const deletedIndex = productImage?.TabIndex ?? 1;
      const fallbackIndex =
        currentProcessUpdatedIndices.find(
          (fi: any) => fi.TabIndex === deletedIndex
        )?.TabIndex ??
        currentProcessUpdatedIndices[currentProcessUpdatedIndices.length - 1]
          ?.TabIndex ??
        1;
      setActiveIndex(fallbackIndex);
      setSelectedImageIndex(fallbackIndex);
    } else {
      setActiveIndex(-1);
      setSelectedImageIndex(-1);
    }

    // 👇 PASTE YOUR CURRENT DELETE LOGIC HERE
    // (everything currently inside your onClick)

    setShowConfirm(false);
    setPendingDeleteData(null);
  };

  const handlePreviewFileDelete = (fileIndex: number) => {
    const selectedProcessIndex = values.Process?.findIndex(
      (p: any) => p.ProcessId === selectedProcessId
    );
    const currentTabData = values.Process[selectedProcessIndex]?.fileIndices?.find(
      (file: any) => file.TabIndex === activeIndex
    );

    // const fileToDeleteForLog = currentTabData?.FileDetail?.find(
    //   (f: any, i: number) => i === fileIndex
    // );
    // console.log("Deleted FilePath (Preview Trash):", fileToDeleteForLog?.FilePath || fileToDeleteForLog?.S3FilePath);

    if ((currentTabData?.FileDetail?.length || 0) <= 1) {
      setPendingDeleteData({
        action: 'file',
        values,
        selectedProcessId,
        fileIndex,
        tabIndex: activeIndex,
        setFieldValue,
      });
      setShowConfirm(true);
      return;
    }

    const selectedFileIndex = (
      values.Process[selectedProcessIndex]?.fileIndices ?? []
    ).findIndex((file: any) => file.TabIndex === activeIndex);

    const foundFile = currentTabData?.FileDetail?.find(
      (f: any, i: number) => i === fileIndex
    );

    const { newDeletedFiles, oldDeletedFiles } = collectMatchingDeletedFiles(
      values.Process,
      foundFile,
      {
        commonOnly: true,
        tabHeading: currentTabData?.FileDetail?.[0]?.Heading,
        exactIdentityOnly: true,
      }
    );

    const mergedNewDeleted = mergeUniqueProductFiles(
      values?.NewDeletedFiles || [],
      newDeletedFiles
    );
    const mergedOldDeleted = mergeUniqueProductFiles(
      values?.OldDeletedFiles || [],
      oldDeletedFiles
    );

    setFieldValue(`NewDeletedFiles`, mergedNewDeleted);
    setFieldValue(`OldDeletedFiles`, mergedOldDeleted);

    setNewDeletedFiles(mergedNewDeleted);
    setOldDeletedFiles(mergedOldDeleted);

    const remainingFiles = [
      ...(currentTabData?.FileDetail?.filter(
        (_: any, i: number) => i !== fileIndex
      ) || []),
    ];

    setFieldValue(
      `Process.${selectedProcessIndex}.fileIndices.${selectedFileIndex}.FileDetail`,
      remainingFiles
    );

    const updatedFileIndices = (
      values.Process[selectedProcessIndex]?.fileIndices ?? []
    ).map((file: any, index: number) =>
      index === selectedFileIndex
        ? { ...file, FileDetail: remainingFiles }
        : file
    );
    setFieldValue(
      'CommonProcess',
      updatedFileIndices.filter((fi: any) => fi.IsDefault || fi.isDefault)
    );
    setSelectedImage(remainingFiles);
  };

  useEffect(() => {
    setPreview(null);
    setFile(null);
    setFieldValue('file', null);
    setPreviewImageIndex(0);

    // Load heading from current file details
    const currentHeading = values.Process?.find(
      (p) => p.ProcessId === selectedProcessId
    )?.fileIndices?.find((p) => p.TabIndex === activeIndex)?.FileDetail?.[0]
      ?.Heading;

    setHeading(currentHeading ?? '');
    if (headingInputRef.current) {
      headingInputRef.current.value = currentHeading ?? '';
    }
  }, [activeIndex, selectedImageIndex, values.Process, selectedProcessId]);

  // Safe error message extraction
  const getErrorMessage = (error: any): string => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && error.message)
      return error.message;
    return String(error);
  };
  //#endregion Local Functions

  useEffect(() => {
    const existingFile = values.Process?.find(
      (p) => p.ProcessId === selectedProcessId
    )
      ?.fileIndices?.find((p) => p.TabIndex === activeIndex)
      ?.FileDetail?.some((item: any) => item.S3FilePath);
  }, [selectedProcessId, activeIndex, values.Process]);

  useEffect(() => {
    if (addProductFileState.success) {
      toast.success(formatMessage('added_success'));
      resetAddProductFileState();
      setCopiedImagesUrl([]);
      setIsCopied(false);
      setFieldValue('SelectedCopyProductImage', -1);
    }
  }, [addProductFileState.success]);

  useEffect(() => {
    if (addProductFileState.error) {
      toast.error(formatMessage(addProductFileState.error));
      resetAddProductFileState();
    }
  }, [addProductFileState.error]);

  useEffect(() => {
    const existingFile = values.Process?.find(
      (p) => p.ProcessId === selectedProcessId
    )
      ?.fileIndices?.find((p) => p.TabIndex === activeIndex)
      ?.FileDetail?.some((item: any) => item.S3FilePath);

    if (existingFile) {
      setShowSave(false); // hide save button if old image exists
    }
  }, [selectedProcessId, activeIndex, values.Process]);

  useEffect(() => {
    if (file || preview) {
      setShowSave(true);
    }
  }, [file, preview]);

  useEffect(() => {
    if (isCopyPanelOpen) {
      GetAllProductService(Number(getTenantIdFromLocalStorage()));
    }
  }, [isCopyPanelOpen, GetAllProductService]);

  // Auto-load first option when copy panel opens
  useEffect(() => {
    if (isCopyPanelOpen && defaultCopyProductImageValue !== undefined) {
      setFieldValue('SelectedCopyProductImage', defaultCopyProductImageValue);
      if (defaultCopyProductImageValue > 0) {
        GetProductImagesByIdService(
          Number(getTenantIdFromLocalStorage()),
          defaultCopyProductImageValue
        );
      } else {
        // Clear copied images when "Please Select" is chosen
        setCopiedImagesUrl([]);
      }
    }
  }, [isCopyPanelOpen, defaultCopyProductImageValue]);

  useEffect(() => {
    if (
      values?.Process?.length > 0 &&
      values?.Process?.filter(
        (e) => e?.ProcessId != -3 && e?.ProcessId != -1 && e?.ProcessId != -2
      ).map((e) => e?.ProcessId).length > 0
    ) {
      GetDimensionByProcessIdService(
        values?.Process?.filter(
          (e) => e?.ProcessId != -3 && e?.ProcessId != -1 && e?.ProcessId != -2
        ).map((e) => e?.ProcessId),
        setFieldValue,
        values
      );
    }
  }, []);

  useEffect(() => {
    const tenantId = Number(
      getTenantIdFromLocalStorage() || getTenantAccessFromLocalStorage() || -1
    );
    if (tenantId > 0) {
      GetDimensionColumnService(tenantId);
    }
  }, [GetDimensionColumnService]);

  useEffect(() => {
    if (
      !Array.isArray(getDimensionTableColumnKeys) ||
      getDimensionTableColumnKeys.length === 0
    ) {
      return;
    }

    if (
      Array.isArray(values?.Dimension?.SelectedColumns) &&
      values.Dimension.SelectedColumns.length > 0
    ) {
      return;
    }

    setFieldValue(`Dimension.SelectedColumns`, [
      ...getDimensionTableColumnKeys.map((col) => ({
        id: Number(col.Id),
        value: Number(col.Id),
        label: col.Messages,
        disabled: false,
        data: col,
      })),
    ]);
    // setFieldValue(`Dimension.TableData`, []);
  }, [getDimensionTableColumnKeys, setFieldValue, values?.Dimension?.SelectedColumns]);

  return (
    <div
      className="tab-content"
      id="myTabContent"
    >
      {showConfirm && (
        <ConfirmDialog
          show={showConfirm}
          title={formatMessage('delete_tab')}
          message={formatMessage("message_delete_tab_confirm")}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
      <div
        className="tab-pane fade show active"
        id="home"
        role="tabpanel"
        aria-labelledby="home-tab"
      >
        <div className="row">
          <div className={selectedProcessId === -3 ? 'col-lg-12' : 'col-lg-3'}>
            {
              selectedProcessId === -3 && (
                <div className="picture_frame">
                  <div className="d-flex">
                    <div
                      key={`pdf_file_${selectedProcessId}_${activeIndex}`}
                      className="pdf_file g-0 row"
                    >
                      {
                        <div className="col-6">
                          <div className="tab-view-common p-2 rounded">
                            <div className="pic_name_list">
                              {values.Process?.find(
                                (p) => p.ProcessId === selectedProcessId
                              )?.fileIndices &&
                                values.Process.find(
                                  (p) => p.ProcessId === selectedProcessId
                                )?.fileIndices?.length > 0 &&
                                values.Process.find(
                                  (p) => p.ProcessId === selectedProcessId
                                )?.fileIndices.map(
                                  (
                                    productImage: IFileIndices,
                                    index: number
                                  ) => {
                                    const tabHeading =
                                      getCommonImageTabHeading(productImage);
                                  
                                    return (
                                      <div
                                        key={index}
                                        title={tabHeading}
                                        className={`textTabBlock show-Elipses ${
                                          productImage.TabIndex === activeIndex
                                            ? 'active'
                                            : ''
                                        } ${activeIndex !== -1 ? '' : 'disabled-div'}`}
                                        onClick={() => {
                                          handleClick(
                                            productImage.TabIndex,
                                            productImage?.FileDetail
                                          );
                                        }}
                                      >
                                        <span
                                          className="pic_name"
                                          // onClick={() =>
                                          //   handleClick(index, productImage?.FileDetail)
                                          // }
                                        >
                                          {tabHeading}
                                        </span>
                                        <span className="closeTabIocn">
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="32"
                                            height="32"
                                            fill="currentColor"
                                            className="bi bi-x delete-icon"
                                            viewBox="0 0 16 16"
                                            onClick={() => {
                                              productImage?.FileDetail?.forEach((f: any) => {
                                                console.log("Deleted FilePath (Tab Close):", f.FilePath || f.S3FilePath);
                                              });
                                              setPendingDeleteData({
                                                values,
                                                selectedProcessId,
                                                productImage,
                                                setFieldValue,
                                              });

                                              setShowConfirm(true);
                                            }}
                                          >
                                            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                                          </svg>
                                        </span>
                                      </div>
                                    );
                                  }
                                )}

                              <div
                                key={commonTabLimit}
                                className={`textTabBlockAdd addImageProduct     ${isCommonTabLimitReached ? 'disable-tab ' : ''} ${!hasActiveExistingCommonTab && activeIndex > 0 ? 'active' : ''} `}
                              >
                                <span
                                  className="pic_name"
                                  onClick={handleAddNewCommonTab}
                                >
                                  <svg
                                    height="14"
                                    viewBox="0 0 512 512"
                                    width="14"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g
                                      id="_03_Login"
                                      data-name="03 Login"
                                    >
                                      <path d="m256 512a25 25 0 0 1 -25-25v-462a25 25 0 0 1 50 0v462a25 25 0 0 1 -25 25z" />
                                      <path d="m487 281h-462a25 25 0 0 1 0-50h462a25 25 0 0 1 0 50z" />
                                    </g>
                                  </svg>
                                </span>
                              </div>
                              {isCommonTabLimitReached && (
                                // <InfoCircleFill
                                //   className="text-muted ms-1"
                                //   title={formatMessage('max_tabs_allowed')}
                                //    title="Minimum of 5 image tabs allowed."
                                // />
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {formatMessage('max_tabs_allowed')}
                                    </Tooltip>
                                  }
                                >
                                  <span className="normal-info-svg">
                                    <InfoCircle size={14} />
                                  </span>
                                </OverlayTrigger>
                                // <span className="normal-info-svg">
                                //   <InfoCircle
                                //     size={14}
                                //     title={formatMessage('max_tabs_allowed')}
                                //   />
                                // </span>
                              )}
                            </div>
                            <figure className="picture">
                              <div
                                className={`tab-view ${
                                  values.Process?.find(
                                    (p) => p.ProcessId === selectedProcessId
                                  )?.fileIndices?.length < activeIndex
                                    ? 'class1'
                                    : 'preview-save-images-drag'
                                }`}
                              >
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                  <div className="form-group w-75">
                              <Form.Control
                                type="text"
                                placeholder={formatMessage('Heading')}
                                name="heading"
                                ref={headingInputRef}
                                value={heading}
                                maxLength={HEADING_MAX_LENGTH}
                                isInvalid={
                                  isDuplicateHeading ||
                                  isHeadingEmptyWithFile ||
                                  String(heading || '').length >=
                                    HEADING_MAX_LENGTH
                                }
                                // disabled={
                                //   !(
                                //     values.Process?.find(
                                //             (p) =>
                                //               p.ProcessId === selectedProcessId
                                //           )?.fileIndices?.length < activeIndex
                                //         )
                                //       }
                                onChange={(e) => {
                                  const val = e.target.value.slice(
                                    0,
                                    HEADING_MAX_LENGTH
                                  );
                                  setHeading(val);
                                  updateActiveCommonTabHeading(val);
                                  const duplicate =
                                    hasDuplicateHeadingInCommonTabs(val);
                                  setIsEnable(
                                    val.length > 0 && !duplicate
                                  );
                                }}
                                    />
                              {isHeadingEmptyWithFile && !isDuplicateHeading && (
                                <Form.Control.Feedback type="invalid">
                                  {formatMessage('please_enter_heading')}
                                </Form.Control.Feedback>
                              )}
                              {isDuplicateHeading && (
                                <Form.Control.Feedback type="invalid">
                                  {formatMessage('duplicate_found_tabs')}
                                </Form.Control.Feedback>
                              )}
                              {!isDuplicateHeading && !isHeadingEmptyWithFile &&
                                String(heading || '').length >=
                                  HEADING_MAX_LENGTH && (
                                  <Form.Control.Feedback type="invalid">
                                 {formatMessage('max_char_limit_256')}
                                  </Form.Control.Feedback>
                                )}
                                  </div>
                                  <label className="d-flex justify-content-between mb-2">
                                    <span
                                      className={`ps-2 slide-copied ${isCopied ? 'copied-active' : ''}`}
                                      onClick={() => {
                                        setIsCopied(true);
                                        setIsOpen(true); // your existing action
                                      }}
                                    >
                                      <small className="pe-2">
                                        {formatMessage('copy_images')}
                                      </small>
                                      <Copy />
                                    </span>
                                  </label>
                                </div>
                                {previewOpen && selectedImage && (
                                  <div className="image-preview-overlay">
                                    <div className="images-overlay-inner">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (previewImageIndex > 0) {
                                            setPreviewImageIndex(
                                              previewImageIndex - 1
                                            );
                                          }
                                        }}
                                        className="nav-btn prev-btn"
                                      >
                                        &#10094;
                                      </button>
                                      <div className="close-overlay-img close-btn">
                                        <span
                                          className="close-btn-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewOpen(false);
                                          }}
                                        >
                                          &times;
                                        </span>
                                      </div>
                                      <div
                                        className="close-overlay-img close-btn"
                                        style={{ right: '60px' }}
                                        title={formatMessage('copy_images')}
                                      >
                                        <span
                                          className="close-btn-icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const imageToCopy = Array.isArray(
                                              selectedImage
                                            )
                                              ? selectedImage[previewImageIndex]
                                              : (selectedImage as any);

                                            if (imageToCopy) {
                                              setSelectedImage((prev: any) =>
                                                Array.isArray(prev)
                                                  ? [...prev, imageToCopy]
                                                  : [prev, imageToCopy]
                                              );
                                              handlePaste(
                                                setFieldValue,
                                                imageToCopy
                                              );
                                              setPreviewOpen(false);
                                            }
                                          }}
                                        >
                                          <Clipboard size={18} />
                                        </span>
                                      </div>
                                      {Array.isArray(selectedImage) &&
                                      selectedImage.length > 0 ? (
                                        <div
                                          className="preview-item"
                                          style={{
                                            // minWidth: 240,
                                            // maxWidth: 520,
                                            flex: '0 0 auto',
                                          }}
                                        >
                                          {selectedImage[previewImageIndex]
                                            ?.FileType !== 'application/pdf' ? (
                                            <img
                                              src={
                                                selectedImage[previewImageIndex]
                                                  ?.S3FilePath
                                              }
                                              draggable={false}
                                              alt={
                                                selectedImage[previewImageIndex]
                                                  ?.Heading ??
                                                `preview-${previewImageIndex}`
                                              }
                                              className="full-preview-img"
                                              style={{
                                                height: 'auto',
                                                display: 'block',
                                              }}
                                            />
                                          ) : (
                                            <iframe
                                              src={`${selectedImage[previewImageIndex]?.S3FilePath}#toolbar=0`}
                                              title={`PDF Viewer ${previewImageIndex}`}
                                              className="full-preview-pdf"
                                              style={{
                                                height: 'auto',
                                                border: 'none',
                                              }}
                                            />
                                          )}
                                        </div>
                                      ) : selectedImage &&
                                        (selectedImage as any).FileType !==
                                          'application/pdf' ? (
                                        <img
                                          src={
                                            (selectedImage as any).S3FilePath
                                          }
                                          draggable={false}
                                          alt="Full preview"
                                          className="full-preview-img"
                                        />
                                      ) : (
                                        selectedImage && (
                                          <iframe
                                            src={`${(selectedImage as any).S3FilePath}#view=FitH&zoom=page-width&toolbar=0`}
                                            title="PDF Viewer"
                                            className="full-preview-pdf"
                                          />
                                        )
                                      )}
                                      <button
                                        className="nav-btn next-btn"
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (
                                            previewImageIndex <
                                            selectedImage.length - 1
                                          ) {
                                            setPreviewImageIndex(
                                              previewImageIndex + 1
                                            );
                                          }
                                        }}
                                      >
                                        &#10095;
                                      </button>
                                    </div>
                                  </div>
                                )}

                                <div
                                  className={`preview-img common-preview position-relative bg-white`}
                                >
                                  <div className="copied-block-drag">
                                    <DragDropFileInput
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      headingInputRef={headingInputRef}
                                      props={props}
                                      setIsEnable={setIsEnable}
                                      fileInputRef={fileInputRef}
                                      setFile={setFile}
                                      setPreview={setPreview}
                                      preview={preview}
                                      restrictedType={getTabLockedType()}
                                    />
                                  </div>

                                  {preview &&
                                    (!isPreviewPdf ? (
                                      <div
                                        className="drag-image relative group cursor-pointer"
                                        onClick={() =>
                                          fileInputRef.current?.click()
                                        }
                                      >
                                        <img
                                          src={preview}
                                          draggable={false}
                                          alt="chosen"
                                          className="img-thumbnail border-0 w-100 drag-product-img"
                                        />
                                        <div className="overlay-text group-hover:opacity-100">
                                          {formatMessage('drag_drop')}{' '}
                                          <a>{formatMessage('click_browse')}</a>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className="drag-image relative group cursor-pointer"
                                        onClick={() =>
                                          fileInputRef.current?.click()
                                        }
                                      >
                                        <iframe
                                          src={preview}
                                          title="PDF Viewer"
                                        />
                                        {/* <div className="overlay-text group-hover:opacity-100">
                                          {formatMessage('drag_drop')}{' '}
                                          <a>{formatMessage('click_browse')}</a>
                                        </div> */}
                                      </div>
                                    ))}
                                </div>
                                {
                                  <div className="text-center mt-3 pt-2 save-drag-images">
                                    <Button
                                      type="button"
                                      variant="primary"
                                      onClick={() => {
                                        const selectedProcessIndex =
                                          values.Process?.findIndex(
                                            (p: any) =>
                                              p.ProcessId === selectedProcessId
                                          );
                                        if (isDuplicateHeading) {
                                          toast.error(
                                            formatMessage('duplicate_found_tabs')
                                          );
                                          return;
                                        }
                                        if (values.file && heading.length > 0) {
                                          const lockedType = getTabLockedType();
                                          const isNewPdf =
                                            values.file.type ===
                                            'application/pdf';
                                          if (
                                            lockedType &&
                                            ((lockedType === 'pdf' &&
                                              !isNewPdf) ||
                                              (lockedType === 'image' &&
                                                isNewPdf))
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                lockedType === 'pdf'
                                                  ? 'invalid_pdf_file_format'
                                                  : 'supported_only_image_format'
                                              )
                                            );
                                            return;
                                          }
                                          UploadProductFileService(
                                            Number(
                                              getTenantAccessFromLocalStorage()
                                            ),
                                            {
                                              File: values.file,
                                              Heading: heading,
                                            },
                                            selectedImage[
                                              selectedImage.length - 1
                                            ],
                                            activeIndex,
                                            setFieldValue,
                                            selectedProcessId,
                                            values,
                                            selectedProcessIndex,
                                            setSelectedImageIndex,
                                            false,
                                            true
                                          );
                                          // setHeading('');
                                          if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                          }
                                          if (headingInputRef.current) {
                                            headingInputRef.current.value = '';
                                          }
                                          setIsEnable(false);
                                          setPreview(null);
                                          setFile(null);
                                          setSelectedImage([]);
                                          setCopiedImagesUrl([]);
                                          setFieldValue('SelectedCopyProductImage', -1);
                                          setFieldValue('file', null);
                                          setShowSave(false);
                                        } else if (
                                          preview &&
                                          selectedImage[
                                            selectedImage.length - 1
                                          ]?.FileId > 0 &&
                                          heading.length > 0
                                        ) {
                                          const lockedType = getTabLockedType();
                                          const isCopyPdf =
                                            selectedImage[
                                              selectedImage.length - 1
                                            ].FileType === 'application/pdf';
                                          if (
                                            lockedType &&
                                            ((lockedType === 'pdf' &&
                                              !isCopyPdf) ||
                                              (lockedType === 'image' &&
                                                isCopyPdf))
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                lockedType === 'pdf'
                                                  ? 'invalid_pdf_file_format'
                                                  : 'supported_only_image_format'
                                              )
                                            );
                                            return;
                                          }
                                          UploadFileCopyService(
                                            Number(
                                              getTenantAccessFromLocalStorage()
                                            ),
                                            selectedImage[
                                              selectedImage.length - 1
                                            ],
                                            heading,
                                            activeIndex,
                                            setFieldValue,
                                            selectedProcessId,
                                            values,
                                            selectedProcessIndex,
                                            setSelectedImageIndex,
                                            false,
                                            true
                                          ).then(() => {
                                            // Keep focus on a real tab after copy-save.
                                            // Never move beyond the configured max common tabs.
                                            const nextActiveIndex = Math.min(
                                              Math.max(activeIndex, 1),
                                              commonTabLimit
                                            );
                                            setActiveIndex(nextActiveIndex);
                                            setSelectedImageIndex(
                                              nextActiveIndex
                                            );
                                            // setFieldValue('file', selectedImage);
                                            // setHeading('');
                                            if (fileInputRef.current) {
                                              fileInputRef.current.value = '';
                                            }
                                            if (headingInputRef.current) {
                                              headingInputRef.current.value =
                                                '';
                                            }
                                            setIsEnable(false);
                                            setPreview(null);
                                            setFile(null);
                                            setSelectedImage([]);
                                            setCopiedImagesUrl([]);
                                            setFieldValue(
                                              'SelectedCopyProductImage',
                                              -1
                                            );
                                            setFieldValue('file', null);
                                            setShowSave(false);
                                          });
                                        } else {
                                          if (
                                            !heading &&
                                            heading.length === 0
                                          ) {
                                            toast.error(
                                              formatMessage(
                                                'please_enter_heading'
                                              )
                                            );
                                          } else {
                                            toast.error(
                                              formatMessage(
                                                'please_select_image'
                                              )
                                            );
                                          }
                                        }
                                      }}
                                      disabled={
                                        addProductFileState?.inProgress ||
                                        isDuplicateHeading
                                      }
                                    >
                                      {formatMessage('save')}
                                    </Button>
                                  </div>
                                }
                              </div>
                            </figure>
                          </div>
                        </div>
                      }
                      {/* <div className="col-6">
                        <div className="saved-images border rounded bg-white">

                             <div className="message-area preview-images-no-data">
                                          <div className="no-data-img">
                                            <img
                                              src={noMessageIcon}
                                              alt=""
                                              className="no_message"
                                            />
                                          </div>
                                          <div>
                                            <p className="">
                                             No Preview Imgaes
                                            </p>
                                          </div>
                                        </div>
                          {values.Process?.find(
                            (p) => p.ProcessId === selectedProcessId
                          )
                            ?.fileIndices?.find(
                              (p) => p.TabIndex === activeIndex
                            )
                            ?.FileDetail?.map(
                              (productDetail: IProductFiles, index: number) => {
                                return (
                                  <div className="">
                                    {!productDetail.S3FilePath ? (
                                      <img
                                        width="100%"
                                        height="300px"
                                        src={img404}
                                        alt={formatMessage('picture_label')}
                                        className=""
                                      />
                                    ) : productDetail.FileType ===
                                      'application/pdf' ? (
                                      <div className='IframeProduct position-relative d-inline-block"'>
                                        <button
                                          type="button"
                                          className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                        >
                                          <DeleteIcon />
                                        </button>
                                        <iframe
                                          src={`${productDetail.S3FilePath}#toolbar=0`}
                                          title="PDF Viewer"
                                          // width="100%"
                                          // height="300px"
                                          style={{ border: 'none' }}
                                        />
                                      </div>
                                    ) : (
                                      <figure
                                        key={index}
                                        className="position-relative d-inline-block"
                                      >
                                        {' '}
                                        <button
                                          type="button"
                                          className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                        >
                                          <DeleteIcon />
                                        </button>
                                        <img
                                          // width="100%"
                                          // height="300px"
                                          src={productDetail.S3FilePath}
                                          alt={formatMessage('picture_label')}
                                          className=""
                                        />
                                      </figure>
                                    )}
                                  </div>
                                );
                              }
                            )}
                        </div>
                      </div> */}
                      <div className="col-6">
                        <div className="saved-images">
                          {fileDetails && fileDetails.length > 0 ? (
                            allFilesAreNull ? (
                              <img
                                width="100%"
                                height="300px"
                                src={img404}
                                draggable={false}
                                alt={formatMessage('picture_label')}
                              />
                            ) : (
                              validFiles.map(
                                (
                                  productDetail: IProductFiles,
                                  index: number
                                ) => {
                                  return (
                                    <>
                                    <div key={index}>
                                      {productDetail.FileType ===
                                      'application/pdf' ? (
                                        <div className="IframeProduct position-relative">
                                          <button
                                            type="button"
                                            className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                            onClick={() => {
                                              handlePreviewFileDelete(index);
                                            }}
                                          >
                                            <DeleteIcon />
                                          </button>
                                          {productDetail.S3FilePath &&
                                          productDetail.S3FilePath !== 'null' &&
                                          productDetail.S3FilePath !==
                                            'undefined' ? (
                                            <iframe
                                              src={`${productDetail.S3FilePath}#toolbar=0`}
                                              title="PDF Viewer"
                                              style={{ border: 'none' }}
                                            />
                                          ) : (
                                            <img
                                              src={img404}
                                              draggable={false}
                                              alt={formatMessage('picture_label')}
                                            />
                                          )}
                                        </div>
                                      ) : (
                                        <figure className="IframeProduct position-relative">
                                          <button
                                            type="button"
                                            className="btn-light-danger-new btn-light-danger images-close position-absolute rounded-circle"
                                            onClick={() => {
                                              handlePreviewFileDelete(index);
                                            }}
                                          >
                                            <DeleteIcon />
                                          </button>
                                      {productDetail.S3FilePath &&
                                      productDetail.S3FilePath !== 'null' &&
                                      productDetail.S3FilePath !==
                                        'undefined' ? (
                                        <img
                                          src={productDetail.S3FilePath}
                                          draggable={false}
                                          alt={formatMessage('picture_label')}
                                        />
                                      ) : (
                                        <img
                                          src={img404}
                                          draggable={false}
                                          alt={formatMessage('picture_label')}
                                        />
                                      )}
                                        </figure>
                                      )}
                                    </div>
                                    </>
                                  );
                                }
                              )
                            )
                          ) : (
                            <div className="message-area preview-images-no-data">
                              <div className="no-data-img">
                                <img
                                  src={noMessageIcon}
                                  draggable={false}
                                  alt=""
                                  className="no_message"
                                />
                              </div>
                              <div>
                                <p>No Preview Images</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {isCopyPanelOpen && (
                      <div
                        className={`close-images-list ${isCopied ? 'copied-active' : ''}`}
                      >
                        <div className="d-flex justify-content-end">
                          <div
                            onClick={() => {
                              setIsCopyPanelOpen(true);
                              setIsCopied(false);
                              setIsFullWidth(true);
                              setFieldValue('SelectedCopyProductImage', -1);
                              setCopiedImagesUrl([]);
                            }}
                            className="btn btn-danger btn-sm close-copied-window"
                          >
                            {' '}
                            {formatMessage('close')} &times;
                          </div>
                        </div>

                        <Field
                          className=""
                          name="SelectedCopyProductImage"
                          autoComplete="off"
                          placeholder=""
                          as={FormikFormSearchSelectField}
                          disabled={false}
                          spaceAllowed={false}
                          isClearable={true}
                          options={allProductSearchSelectOptions}
                          typeofoptionid={'number'}
                          changeHandler={(option: any) => {
                            if (
                              option > 0 &&
                              option !== values.SelectedCopyProductImage
                            ) {
                              GetProductImagesByIdService(
                                Number(getTenantIdFromLocalStorage()),
                                option
                              );
                            } else {
                              setFieldValue('SelectedCopyProductImage', -1);
                              setCopiedImagesUrl([]);
                            }
                          }}
                        />
                        <ul className="border copy-images-block">
                          {getCopiedImagesUrl?.length > 0 ? (
                            getCopiedImagesUrl.map(
                              (copiedImage: IProductFiles, index: number) => {
                                return (
                                  <li
                                    className={
                                      selectedImage[selectedImage.length - 1]
                                        ?.FileId === copiedImage?.FileId
                                        ? ``
                                        : ``
                                    }
                                    key={index}
                                  >
                                    {copiedImage?.S3FilePath ? (
                                    <div className="position-relative d-inline-block images-copied-done">
                                      <div
                                        className="copy-image  top-0 end-0 rounded-circle bg-light shadow-sm"
                                        onClick={() => {
                                          // setActiveIndex(index);
                                          // setSelectedImageIndex(index);
                                          setSelectedImage((prev) => [
                                            ...prev,
                                            copiedImage,
                                          ]);
                                          handlePaste(
                                            setFieldValue,
                                            copiedImage
                                          );
                                        }}
                                        title="Copy image"
                                      >
                                        <Clipboard
                                          size={18}
                                          title="copy image"
                                        />
                                      </div>
                                      <div
                                        className="images-copied-done1 cursor-pointer"
                                        onClick={() => {
                                          // Open full copied-images list in preview and start at clicked index
                                          setSelectedImage(
                                            getCopiedImagesUrl || [copiedImage]
                                          );
                                          setPreviewImageIndex(index);
                                          setPreviewOpen(true);
                                        }}
                                      >
                                        {copiedImage?.FileType !==
                                        'application/pdf' ? (
                                          copiedImage?.S3FilePath &&
                                          copiedImage?.S3FilePath !== 'null' &&
                                          copiedImage?.S3FilePath !==
                                            'undefined' ? (
                                            <img
                                              className="border"
                                              width={100}
                                              height={100}
                                              src={copiedImage?.S3FilePath}
                                              draggable={false}
                                              alt="preview"
                                            />
                                          ) : (
                                            <img
                                              className="border"
                                              width={100}
                                              height={100}
                                              src={img404}
                                              draggable={false}
                                              alt="preview"
                                            />
                                          )
                                        ) : copiedImage?.S3FilePath &&
                                          copiedImage?.S3FilePath !== 'null' &&
                                          copiedImage?.S3FilePath !==
                                            'undefined' ? (
                                          <iframe
                                            src={`${copiedImage?.S3FilePath}#toolbar=0`}
                                            title="PDF Viewer"
                                            style={{
                                              border: 'none',
                                              width: 100,
                                              height: 100,
                                              pointerEvents: 'none',
                                            }}
                                          />
                                        ) : (
                                          <img
                                            className="border"
                                            width={100}
                                            height={100}
                                            src={img404}
                                            draggable={false}
                                            alt="preview"
                                          />
                                        )}
                                      </div>
                                    </div>
                                    ) : (
                                      <img
                                      src={img404}
                                      draggable={false}
                                      alt={formatMessage('picture_label')}
                                    />
                                    )}
                                  </li>
                                );
                              }
                            )
                          ) : (
                            <li className="messageCopied">
                              <div className="message-area">
                                <div className="no-data-img">
                                  <img
                                    src={noMessageIcon}
                                    draggable={false}
                                    alt=""
                                    className="no_message"
                                  />
                                </div>
                                <div>
                                  <p className="">
                                    {formatMessage('no_copy_image')}
                                  </p>
                                </div>
                              </div>
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )
              //   : (
              //   <div className="dimension-process">
              //     <div className="form-group formSet">
              //       <Label
              //         required={false}
              //         label="select_process"
              //       />
              //       <Field
              //         className="form-control"
              //         name="SelectedProcessForDimensionTable"
              //         as={FormikFormSelect}
              //         options={selectedProcessOptions}
              //         onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              //           setFieldValue(
              //             'SelectedProcessForDimensionTable',
              //             Number(e.target.value)
              //           );
              //         }}
              //       />
              //     </div>
              //     <div className="form-group formSet">
              //       <Label
              //         required={false}
              //         label="select_column"
              //       />
              //       <Field
              //         name={`Process.${selectedProcessIndx}.DimensionDetails.SelectedColumns`}
              //         className="react-select-parent"
              //         isMulti={true}
              //         isSearchable={true}
              //         isDisabled={false}
              //         padding
              //         isLoading={false}
              //         isClearable={true}
              //         placeholder={formatMessage('please_select')}
              //         options={[
              //           ...getDimensionTableColumnKeys.map((col) => ({
              //             id: Number(col.Id),
              //             value: Number(col.Id),
              //             label: col.Messages,
              //             disabled: false,
              //             data: col,
              //           })),
              //         ]}
              //         as={FormikFormInputMultipleSelect}
              //       />
              //     </div>
              //     <div className="text-center mt-5">
              //       {getDimensionTableColumnKeys.length > 0 && <></>}
              //     </div>
              //   </div>
              // )
            }
          </div>
          <div className={'col-lg-12'}>
            {selectedProcessId === -4 && (
              <CommonImageTabView
                id={id}
                isDimensionTable={false}
                processFileDetails={values.Process?.find(
                  (p) => p.ProcessId === selectedProcessId
                )}
                processType={
                  values.Process?.find((p) => p.ProcessId === selectedProcessId)
                    ?.Type
                }
                selectedProcess={selectedProcessId}
                selectedImage={selectedImage}
                selectedImageIndex={selectedImageIndex}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default observer(CommonProcessTabContent);
